import useGetTransferWorkspace from 'api/operation/transfer/useGetTransferWorkspace';
import useTransferWorkspaceMutations from 'api/operation/transfer/useTransferWorkspaceMutations';
import { useCallback, useEffect, useMemo, useState } from 'react';

const useTransferWorkspace = guid => {
  const { getTransferWorkspace, loading, workspace } =
    useGetTransferWorkspace();
  const mutations = useTransferWorkspaceMutations();
  const [actionLoading, setActionLoading] = useState(false);

  const canRefetch = useMemo(
    () => guid && location.pathname.startsWith('/operation/transfer'),
    [guid],
  );

  useEffect(() => {
    if (!canRefetch) return;
    getTransferWorkspace(guid);
  }, [guid, getTransferWorkspace, canRefetch]);

  const refreshWorkspace = useCallback(() => {
    if (!guid) return Promise.resolve(null);
    return getTransferWorkspace(guid);
  }, [getTransferWorkspace, guid]);

  useEffect(() => {
    if (!canRefetch) return;
    window.addEventListener('refreshOperationList', refreshWorkspace);
    return () => {
      window.removeEventListener('refreshOperationList', refreshWorkspace);
    };
  }, [refreshWorkspace]);

  const canSubmitOperation = useMemo(() => {
    return workspace?.state === 2;
  }, [workspace?.state]);

  const withAction = useCallback(
    async action => {
      setActionLoading(true);
      try {
        const result = await action();
        if (result) await refreshWorkspace();
        return result;
      } finally {
        setActionLoading(false);
      }
    },
    [refreshWorkspace],
  );

  const state = workspace?.state;
  const title = workspace?.title ?? '';

  const submitOperation = useCallback(
    () => withAction(() => mutations.submitOperation(guid)),
    [guid, mutations, withAction],
  );

  return {
    loading,
    actionLoading,
    workspace,
    title,
    state,
    refreshWorkspace,
    submitOperation,
    canSubmitOperation,
  };
};

export default useTransferWorkspace;
